import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { partnersService, mediaUrl } from '../lib/api-service';
import type { PartnerListItem } from '../lib/types';
import { Search, Handshake, Loader2 } from 'lucide-react';
import { DocumentMeta } from '../hooks/useDocumentMeta';

const STATUS_LABELS: Record<string, string> = {
  public_entity: 'Instituție publică',
  private_company: 'Companie privată',
  ngo: 'ONG',
  informal_group: 'Grup informal',
};

export default function PartnersDirectory() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    partnersService.list()
      .then((data) => setPartners(data.results))
      .finally(() => setLoading(false));
  }, []);

  const filtered = partners.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <DocumentMeta
        title="Prieteni"
        description="Organizațiile prietene ale programului Alba Iulia — Capitala Tineretului din România 2026."
        url="/parteneri"
      />
      <h1 className="mb-6 text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
        Prieteni
      </h1>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          placeholder="Caută prieteni..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-full border py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
          style={{ fontFamily: 'var(--font-secondary)' }}
        />
      </div>

      {/* Partner cards grid */}
      {filtered.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/partener/${p.slug}`)}
              className="flex items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-all hover:shadow-lg active:scale-[0.98]"
            >
              {p.logo ? (
                <img src={mediaUrl(p.logo)} alt="" className="h-14 w-14 rounded-xl border object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--vivid-purple)]/10">
                  <Handshake className="h-6 w-6 text-[var(--vivid-purple)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="truncate text-sm font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
                  {p.name}
                </h3>
                <span
                  className="inline-block mt-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                >
                  {STATUS_LABELS[p.status] || p.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center py-16 text-center">
          <Handshake className="mb-3 h-12 w-12 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Nu am găsit prieteni
          </p>
        </div>
      )}
    </div>
  );
}
